"use client";

import { useState } from "react";
import { CheckoutDialog } from "@/components/checkout_dialog";
import { PricingCardArtistic } from "@/components/pricing/pricing_card_artistic";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import type { PricingRecord } from "@/lib/pricing-data";
import { toCheckoutPlan } from "./checkout_launch";
import PriceMatchDialog from "./price_match_dialog";

export default function EditingPricingTab({
	data,
	country = "usd",
}: {
	data: PricingRecord[];
	country?: string;
}) {
	const [selectedPlan, setSelectedPlan] = useState<PricingRecord | null>(null);
	const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

	const handleGetStarted = (plan: PricingRecord) => {
		setSelectedPlan(plan);
		setIsCheckoutOpen(true);
	};

	return (
		<>
			<CheckoutDialog
				open={isCheckoutOpen}
				onOpenChange={setIsCheckoutOpen}
				plan={selectedPlan ? toCheckoutPlan(selectedPlan) : null}
				currency={country === "inr" ? "inr" : "usd"}
			/>

			<section className="w-full mb-12">
				<div
					className={`grid grid-cols-1 md:grid-cols-2 ${
						(data?.length ?? 0) % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"
					} gap-6 max-w-7xl mx-auto`}
				>
					{(data ?? []).map((pkg, i) => (
						<div key={pkg.id} className="h-full">
							<PricingCardArtistic
								name={pkg.tier || pkg.name}
								price={country === "inr" ? pkg.price_in_inr : pkg.price_in_usd}
								currency={country}
								features={pkg.features}
								popular={pkg.popular}
								onGetStarted={() => handleGetStarted(pkg)}
								index={i}
								description="Professional Editing"
								categoryIcon="scissors"
							/>
						</div>
					))}
				</div>
			</section>

			<PriceMatchDialog />

			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Editing FAQ
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-1"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Turnaround Time
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Standard delivery takes 48 hours. Expedited delivery (24 hours) is available. The clock starts once we receive all necessary footage.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Accepted Formats
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"We accept all major formats including 4K, 8K, RAW, and LOG profiles across all standard codecs.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Revisions Policy
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							We offer unlimited revisions until satisfaction. Feedback is
							streamlined via Frame.io for precise communication.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-4"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Software Stack
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							Our editors utilize the Adobe Creative Cloud suite and DaVinci
							Resolve, ensuring industry-standard quality and color grading.
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
